import type { Basket, BasketItem } from "../basket/store";
import { t } from "../i18n/t";

const SVG_X = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`;
const SVG_SEND = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 2-11 11M22 2l-7 20-4-9-9-4 20-7Z"/></svg>`;

export class BasketDrawer {
  private backdrop: HTMLElement;
  private drawer: HTMLElement;
  private listEl!: HTMLElement;
  private summaryEl!: HTMLElement;
  private submitBtn!: HTMLButtonElement;
  private clearBtn!: HTMLButtonElement;
  private isOpen = false;

  constructor(
    parent: HTMLElement,
    private basket: Basket,
    private onSubmit: () => void,
  ) {
    this.backdrop = document.createElement("div");
    this.backdrop.className = "kb-drawer-backdrop";
    this.backdrop.addEventListener("click", () => this.close());

    this.drawer = document.createElement("aside");
    this.drawer.className = "kb-drawer";
    this.drawer.innerHTML = `
      <div class="kb-drawer-header">
        <div>
          <div class="kb-drawer-title">${t("basket.title")}</div>
          <div class="kb-drawer-sub" data-role="sub"></div>
        </div>
        <button class="kb-drawer-close" aria-label="schließen">${SVG_X}</button>
      </div>
      <div class="kb-drawer-list" data-role="list"></div>
      <div class="kb-drawer-footer">
        <div class="kb-drawer-summary">
          <span class="kb-drawer-summary-label">${t("basket.label")}</span>
          <span class="kb-drawer-summary-val" data-role="total">0</span>
        </div>
        <div class="kb-drawer-actions">
          <button class="kb-drawer-clear" data-role="clear">${t("basket.clear")}</button>
          <button class="kb-drawer-submit" data-role="submit">${SVG_SEND}<span>${t("basket.send")}</span></button>
        </div>
      </div>
    `;
    parent.appendChild(this.backdrop);
    parent.appendChild(this.drawer);

    this.listEl = this.drawer.querySelector('[data-role="list"]') as HTMLElement;
    this.summaryEl = this.drawer.querySelector('[data-role="total"]') as HTMLElement;
    this.submitBtn = this.drawer.querySelector('[data-role="submit"]') as HTMLButtonElement;
    this.clearBtn = this.drawer.querySelector('[data-role="clear"]') as HTMLButtonElement;

    (this.drawer.querySelector(".kb-drawer-close") as HTMLButtonElement).addEventListener(
      "click",
      () => this.close(),
    );
    this.clearBtn.addEventListener("click", () => {
      if (this.basket.rows() === 0) return;
      this.basket.clear();
    });
    this.submitBtn.addEventListener("click", () => {
      if (this.basket.rows() === 0) return;
      this.onSubmit();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) this.close();
    });

    this.basket.subscribe((items) => this.renderList(items));
  }

  open() {
    this.isOpen = true;
    this.backdrop.classList.add("visible");
    this.drawer.classList.add("visible");
  }

  close() {
    this.isOpen = false;
    this.backdrop.classList.remove("visible");
    this.drawer.classList.remove("visible");
  }

  private renderList(items: readonly BasketItem[]) {
    const sub = this.drawer.querySelector('[data-role="sub"]') as HTMLElement;
    if (items.length === 0) {
      sub.textContent = t("basket.subtitle.zero");
      this.listEl.innerHTML = `
        <div class="kb-drawer-empty">
          <div class="kb-empty-art">
            <div class="ring"></div>
            <div class="half"></div>
          </div>
          <h4>${t("basket.empty.title")}</h4>
          <p>${t("basket.empty.body")}</p>
        </div>
      `;
      this.summaryEl.textContent = "0";
      this.submitBtn.disabled = true;
      this.clearBtn.disabled = true;
      return;
    }
    sub.textContent =
      items.length === 1
        ? t("basket.subtitle.one")
        : t("basket.subtitle.many", { n: items.length });
    this.submitBtn.disabled = false;
    this.clearBtn.disabled = false;
    this.summaryEl.textContent = String(items.reduce((s, i) => s + i.qty, 0));

    this.listEl.innerHTML = "";
    for (const item of items) {
      const row = document.createElement("div");
      row.className = "kb-basket-row";
      row.innerHTML = `
        <div class="kb-basket-thumb">
          <span class="kb-basket-thumb-dot" style="background:${item.color}"></span>
        </div>
        <div class="kb-basket-info">
          <div class="kb-basket-name">${escapeHtml(item.name)}</div>
          <div class="kb-basket-art">${item.partId}</div>
        </div>
        <div class="kb-basket-actions">
          <button class="kb-remove" aria-label="entfernen">${SVG_X}</button>
          <div class="kb-qty">
            <button data-act="dec">−</button>
            <input type="number" min="1" max="99" value="${item.qty}" />
            <button data-act="inc">＋</button>
          </div>
        </div>
      `;
      const dec = row.querySelector('[data-act="dec"]') as HTMLButtonElement;
      const inc = row.querySelector('[data-act="inc"]') as HTMLButtonElement;
      const qtyEl = row.querySelector('input[type="number"]') as HTMLInputElement;
      const rm = row.querySelector(".kb-remove") as HTMLButtonElement;

      dec.addEventListener("click", () => this.basket.setQty(item.partId, item.qty - 1));
      inc.addEventListener("click", () => this.basket.setQty(item.partId, item.qty + 1));
      qtyEl.addEventListener("change", () =>
        this.basket.setQty(item.partId, Number(qtyEl.value) || 1),
      );
      rm.addEventListener("click", () => this.basket.remove(item.partId));

      this.listEl.appendChild(row);
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
